import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/app-shell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader2, 
  KeyRound, 
  Save, 
  Coins, 
  Cpu, 
  CheckCircle2, 
  AlertCircle, 
  ArrowUpRight, 
  Zap, 
  Server, 
  Activity, 
  RefreshCw, 
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";

interface AiGatewayStatus {
  gatewayOnline: boolean;
  keySource: string;
  maskedKey: string;
  activeModels: Array<{
    id: string;
    name: string;
    status: string;
    ratePer1k: number;
  }>;
  quota: {
    creditBalance: number;
    tier: string;
    costPer1kTokens: number;
    billingExempt: boolean;
    tenantName: string;
  };
}

interface TestPingResult {
  success: boolean;
  source: string;
  reply: string;
  latencyMs: number;
  tokensUsed: {
    promptTokens: number;
    completionTokens: number;
    total: number;
  };
  deduction: {
    allowed: boolean;
    creditsDeducted: number;
    remainingBalance: number;
  };
}

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const [testResult, setTestResult] = useState<TestPingResult | null>(null);

  // 1. Fetch Application Manager Gateway Status & Customer Quota
  const { 
    data: gatewayStatus, 
    isLoading: isStatusLoading,
    refetch: refetchGatewayStatus 
  } = useQuery<AiGatewayStatus>({
    queryKey: ["/api/admin/settings/ai-gateway"],
  });

  // 2. Test Connection Mutation
  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/settings/ai-gateway/test");
      return (await res.json()) as TestPingResult;
    },
    onSuccess: (data) => {
      setTestResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/ai-gateway"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ai/credits"] });
      toast({
        title: "AI Gateway Verified",
        description: `Ping completed in ${data.latencyMs}ms. Deducted ${data.deduction.creditsDeducted} credits.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Test Connection Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 3. Application Manager Master Key Override Mutation
  const updateSettingMutation = useMutation({
    mutationFn: async (keyStr: string) => {
      const res = await apiRequest("POST", "/api/admin/settings/grok-key", { key: keyStr });
      return await res.json();
    },
    onSuccess: () => {
      toast({ 
        title: "Application Manager Key Updated", 
        description: "Successfully synchronized platform AI key for Pump ECM." 
      });
      setApiKey("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/ai-gateway"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/grok-key"] });
    },
    onError: (error: Error) => {
      toast({ title: "Save Failed", description: error.message, variant: "destructive" });
    }
  });

  if (user?.role !== "Admin") {
    return <Redirect to="/" />;
  }

  const quota = gatewayStatus?.quota;
  const isExempt = quota?.billingExempt || quota?.tier === "ENTERPRISE_UNLIMITED";

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-8 pb-16">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">AI & Token Credit Management</h1>
              <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                Enterprise Suite
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              Centralized Application Manager settings key and customer token credit allocation for all AI models
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => refetchGatewayStatus()}
              disabled={isStatusLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isStatusLoading ? "animate-spin" : ""}`} />
              Refresh Status
            </Button>
            <a 
              href="https://app.onitnetworking.com/application-manager" 
              target="_blank" 
              rel="noreferrer"
            >
              <Button size="sm" className="gap-1.5 shadow-sm">
                <span>Application Manager</span>
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>

        {/* Top Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Customer Token Credit Balance Card */}
          <Card className="border-primary/20 shadow-sm bg-gradient-to-br from-card to-primary/5">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Coins className="h-4 w-4 text-amber-500" />
                  Customer Token Credits
                </CardTitle>
                <Badge variant="secondary" className="font-mono text-xs">
                  {quota?.tier || "PROFESSIONAL_METERED"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {isStatusLoading ? (
                  <span className="text-muted-foreground text-xl">Loading...</span>
                ) : isExempt ? (
                  <span className="text-emerald-600 flex items-center gap-1.5 text-2xl">
                    Unlimited <Sparkles className="h-5 w-5" />
                  </span>
                ) : (
                  <span>{(quota?.creditBalance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isExempt 
                  ? "Enterprise plan with unlimited AI model compute" 
                  : "Metered balance deducted automatically per token run"}
              </p>
            </CardContent>
            <CardFooter className="pt-0 text-xs text-muted-foreground border-t bg-muted/20 py-2.5 px-6 flex justify-between items-center">
              <span>Rate: ${quota?.costPer1kTokens || 0.002} / 1k tokens</span>
              <span className="font-medium text-foreground">{quota?.tenantName || "Facility Account"}</span>
            </CardFooter>
          </Card>

          {/* Application Manager Key Status */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Server className="h-4 w-4 text-blue-500" />
                  Application Manager Key
                </CardTitle>
                {gatewayStatus?.gatewayOnline ? (
                  <Badge className="bg-emerald-600 text-white hover:bg-emerald-700 gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <AlertCircle className="h-3 w-3" /> Inactive
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold font-mono tracking-tight text-foreground">
                {isStatusLoading ? "Checking..." : (gatewayStatus?.maskedKey || "No Key Configured")}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Source: <span className="font-semibold capitalize">{gatewayStatus?.keySource.replace("_", " ") || "Platform Default"}</span>
              </p>
            </CardContent>
            <CardFooter className="pt-0 text-xs text-muted-foreground border-t bg-muted/20 py-2.5 px-6 flex justify-between items-center">
              <span>Encrypted AES-256</span>
              <span className="flex items-center gap-1 text-emerald-600 font-medium">
                <ShieldCheck className="h-3.5 w-3.5" /> Fleet Synchronized
              </span>
            </CardFooter>
          </Card>

          {/* Model Suite Status */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Cpu className="h-4 w-4 text-purple-500" />
                  Active Model Suite
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  4 Models
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                Grok 4.3 + Vision
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Proposals, Pump ECM, OCR, & Copilot RAG
              </p>
            </CardContent>
            <CardFooter className="pt-0 text-xs text-muted-foreground border-t bg-muted/20 py-2.5 px-6 flex justify-between items-center">
              <span>Endpoint: api.x.ai/v1</span>
              <span className="text-primary font-medium">Auto-Routed</span>
            </CardFooter>
          </Card>
        </div>

        {/* Live Diagnostics & Connection Verification */}
        <Card className="shadow-sm border-border/80">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  Live Gateway Diagnostics & Metering Test
                </CardTitle>
                <CardDescription>
                  Verify that Pump ECM connects seamlessly to the Application Manager key and deducts token credits properly.
                </CardDescription>
              </div>
              <Button
                onClick={() => testConnectionMutation.mutate()}
                disabled={testConnectionMutation.isPending}
                className="gap-2 shrink-0"
              >
                {testConnectionMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Testing Connection...</>
                ) : (
                  <><Zap className="h-4 w-4" /> Run Gateway Diagnostic</>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {testResult ? (
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-600 text-white gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Diagnostic Succeeded
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Latency: <strong className="text-foreground">{testResult.latencyMs}ms</strong>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Tokens: <strong className="text-foreground">{testResult.tokensUsed.total}</strong>
                    </span>
                  </div>
                  <span className="text-xs font-mono font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded dark:bg-emerald-950/50">
                    Deducted: {testResult.deduction.creditsDeducted} credits
                  </span>
                </div>
                <div className="text-sm font-mono bg-background p-3 rounded border text-muted-foreground">
                  {testResult.reply}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                  <span>Key Resolved Via: <strong className="capitalize text-foreground">{testResult.source.replace("_", " ")}</strong></span>
                  <span>Updated Credit Balance: <strong className="text-foreground">{testResult.deduction.remainingBalance.toFixed(2)} credits</strong></span>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground border border-dashed rounded-lg">
                Click <strong>"Run Gateway Diagnostic"</strong> to execute a live test ping against xAI Grok and verify token credit deduction.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enterprise Model Capability Matrix */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Cpu className="h-5 w-5 text-indigo-500" />
              Unified Enterprise AI Model Matrix
            </CardTitle>
            <CardDescription>
              All microservices in the ONIT Enterprise Suite route through these shared model configurations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground text-left text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 font-semibold">Model Identifier</th>
                    <th className="py-3 px-4 font-semibold">Purpose & Application</th>
                    <th className="py-3 px-4 font-semibold">Token Rate</th>
                    <th className="py-3 px-4 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-3 px-4 font-mono font-medium text-foreground">grok-4.3</td>
                    <td className="py-3 px-4 text-muted-foreground">NYSERDA OsEM Grant Writing, Pump ECM Recommendations, Study Synthesis</td>
                    <td className="py-3 px-4 font-mono text-xs">$0.004 / 1k tokens</td>
                    <td className="py-3 px-4 text-right">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400">
                        Active
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-mono font-medium text-foreground">grok-2-vision-1212</td>
                    <td className="py-3 px-4 text-muted-foreground">Equipment Nameplate OCR, Pump & Motor Condition Visual Analysis</td>
                    <td className="py-3 px-4 font-mono text-xs">$0.005 / image scan</td>
                    <td className="py-3 px-4 text-right">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400">
                        Active
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-mono font-medium text-foreground">grok-4-1-fast-non-reasoning</td>
                    <td className="py-3 px-4 text-muted-foreground">Rapid Diagnostic Verification, Interactive Copilot Interrogation</td>
                    <td className="py-3 px-4 font-mono text-xs">$0.002 / 1k tokens</td>
                    <td className="py-3 px-4 text-right">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400">
                        Active
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-mono font-medium text-foreground">grok-4-6</td>
                    <td className="py-3 px-4 text-muted-foreground">High-Dimensional Energy Conservation Measures & DAG Optimization</td>
                    <td className="py-3 px-4 font-mono text-xs">$0.006 / 1k tokens</td>
                    <td className="py-3 px-4 text-right">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-400">
                        Enterprise Tier
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Platform Settings Key Override (Admin Console) */}
        <Card className="shadow-sm border-border/70">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-500" />
              Application Manager Key Synchronization
            </CardTitle>
            <CardDescription>
              Update or rotate the centralized platform key stored in <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">system_settings (xai_api_key)</code>. This propagates across all Pump ECM proposal and analysis workers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Active Resolved Key</Label>
              <div className="text-sm font-mono bg-muted p-2.5 rounded-md text-foreground flex items-center justify-between">
                <span>{isStatusLoading ? "Loading..." : (gatewayStatus?.maskedKey || "No key configured")}</span>
                <Badge variant="secondary" className="font-sans text-xs capitalize">
                  {gatewayStatus?.keySource.replace("_", " ")}
                </Badge>
              </div>
            </div>
            
            <div className="space-y-2 pt-2">
              <Label htmlFor="newApiKey">Update Application Manager Master Key</Label>
              <Input
                id="newApiKey"
                type="password"
                placeholder="xai-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Keys are automatically encrypted with AES-256 before persisting. Leave empty to maintain existing configuration.
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between items-center border-t bg-muted/10">
            <span className="text-xs text-muted-foreground">
              Connected to Enterprise Suite Database
            </span>
            <Button 
              onClick={() => updateSettingMutation.mutate(apiKey)} 
              disabled={!apiKey || updateSettingMutation.isPending}
            >
              {updateSettingMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving Key...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" /> Save Application Manager Key</>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </AppShell>
  );
}
